import { useState } from 'react';

import { Btn, ProcessStepper, RoleChip, SkeletonText, useToast } from '@/components/ui';
import { NavIcon } from '@/components/icons/NavIcon';
import type { AsyncState } from '@/hooks';
import { useGrantProcess } from '@/hooks';
import { createGrant, deleteGrant, updateGrant } from '@/api/grants';
import { createGrantRequest, createRevokeRequest } from '@/api/requests';
import type { Level, ProjectMember, ProjectMembersResponse } from '@/api/types';
import { ApiHttpError, getErrorMessage } from '@/api';
import { useMetaContext } from '@/components/Layout';
import { emailLocal, levelLabel } from '@/lib/format';

import AddMemberForm from './AddMemberForm';
import MemberRow from './MemberRow';
import styles from './PermTab.module.css';

interface PermTabProps {
  // grant scope에 박을 식별자 — utils.apps.project_id (5자리 숫자). null이면 게임 데이터 미연동 — 부여 불가.
  projectId: string | null;
  // 표시·시각용 dataset prefix (orbit 디자인 일관성)
  prefix: string;
  membersState: AsyncState<ProjectMembersResponse>;
}

// plan §5.26: ProjectPermTab — orbit ProjectPermTab.jsx 디자인 동치.
// Admin: 즉시 적용 (POST/PATCH/DELETE /grants). 비admin: requests 흐름.
// 역할 변경은 즉시 적용이 아닌 pending → 사용자 명시적 확정으로 우발적 변경 방지.

export default function PermTab({ projectId, prefix, membersState }: PermTabProps) {
  const meta = useMetaContext();
  const isAdmin = meta.current_user.platform_role === 'admin';
  const toast = useToast();

  const [busyEmail, setBusyEmail] = useState<string | null>(null);
  // email -> 새 role (pending). via_all인 경우 변경은 안내만.
  const [pendingChanges, setPendingChanges] = useState<Record<string, Level>>({});
  const [adding, setAdding] = useState(false);
  // 권한 반영 과정 스텝퍼 — MemberPerms와 공유하는 공통 hook.
  const { processSteps, processTitle, runGrantProcess, closeStepper } = useGrantProcess();

  const members = membersState.data?.members ?? [];

  // 멤버 목록 갱신 콜백 — runGrantProcess 성공 시 호출.
  const reloadMembers = () => membersState.reload();

  // adding 모드 토글 시 pending 초기화는 의도적으로 하지 않는다 — 두 작업이 충돌하지 않음.

  // ── admin 즉시 역할 변경 / 비admin 요청 흐름 ──────────────
  const submitRoleChange = async (m: ProjectMember, newLevel: Level) => {
    setBusyEmail(m.email);
    try {
      if (isAdmin) {
        if (m.via_all) {
          // 'all' grant via expand-all 필요 — 안내 후 종료.
          toast.info(
            '이 멤버는 "모든 게임 권한"으로 부여되어 있어 멤버별 권한 페이지에서 전환이 필요해요',
          );
          return;
        }
        const ok = await runGrantProcess(
          `${emailLocal(m.email)}님 역할 변경 중`,
          [() => updateGrant({ email: m.email, scope: m.scope, level: newLevel })],
          reloadMembers,
        );
        if (!ok) return;
      } else {
        if (!projectId) {
          toast.error('게임 데이터 미연동 — 권한 요청 불가');
          return;
        }
        await createGrantRequest({
          email: m.email,
          level: newLevel,
          scope_type: 'game',
          games: [projectId],
          // 인라인 역할 토글엔 사유 입력칸이 없으므로 자동 사유를 채운다.
          // (백엔드 reason 필수 — 누락 시 422)
          reason: `역할 변경: ${levelLabel(m.level)} → ${levelLabel(newLevel)}`,
        });
        toast.success(`${emailLocal(m.email)}님 역할 변경 요청을 보냈어요`);
        membersState.reload();
      }
      setPendingChanges((prev) => {
        const next = { ...prev };
        delete next[m.email];
        return next;
      });
    } catch (err) {
      closeStepper();
      toast.error(getErrorMessage(err));
    } finally {
      setBusyEmail(null);
    }
  };

  const submitRemove = async (m: ProjectMember) => {
    setBusyEmail(m.email);
    try {
      if (isAdmin) {
        if (m.via_all) {
          toast.info(
            '이 멤버는 "모든 게임 권한"으로 부여되어 있어 멤버별 권한 페이지에서 처리해주세요',
          );
          return;
        }
        await runGrantProcess(
          `${emailLocal(m.email)}님 권한 회수 중`,
          [() => deleteGrant({ email: m.email, scope: m.scope })],
          reloadMembers,
        );
      } else {
        if (!projectId) {
          toast.error('게임 데이터 미연동 — revoke 요청 불가');
          return;
        }
        await createRevokeRequest({
          email: m.email,
          level: m.level,
          scope_type: 'game',
          games: [projectId],
          // 인라인 제거 버튼엔 사유 입력칸이 없으므로 자동 사유를 채운다.
          // (백엔드 reason 필수 — 누락 시 422)
          reason: `권한 제거: ${levelLabel(m.level)}`,
        });
        toast.success(`${emailLocal(m.email)}님 제거 요청을 보냈어요`);
        membersState.reload();
      }
    } catch (err) {
      closeStepper();
      toast.error(getErrorMessage(err));
    } finally {
      setBusyEmail(null);
    }
  };

  const submitAddMember = async (emails: string[], level: Level, reason: string) => {
    if (!projectId) {
      toast.error('게임 데이터 미연동 — 멤버 추가 불가');
      return;
    }
    if (emails.length === 0) return;
    setBusyEmail(emails[0]);
    // 단수/복수에 따라 제목·메시지 분기.
    const who =
      emails.length === 1 ? `${emailLocal(emails[0])}님` : `${emails.length}명`;
    try {
      if (isAdmin) {
        const ok = await runGrantProcess(
          `${who} ${levelLabel(level)} 권한 부여 중`,
          emails.map((email) => () => createGrant({ email, scope: projectId, level })),
          reloadMembers,
        );
        if (!ok) return;
        setAdding(false);
      } else {
        for (const email of emails) {
          await createGrantRequest({
            email,
            level,
            scope_type: 'game',
            games: [projectId],
            reason: reason.trim() || undefined,
          });
        }
        toast.success(`${who}을 ${levelLabel(level)}로 추가하는 요청을 보냈어요`);
        setAdding(false);
        membersState.reload();
      }
    } catch (err) {
      closeStepper();
      if (err instanceof ApiHttpError && err.status === 403) {
        toast.error('Admin이 아니므로 이 동작을 직접 실행할 수 없어요');
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setBusyEmail(null);
    }
  };

  return (
    <div>
      {/* ── 역할 가이드 ───────────────────────────────────── */}
      <div className={styles.guide}>
        <div className={styles.guideGrid}>
          <div className={styles.guideRow}>
            <RoleChip role="editor" />
            <div style={{ font: '600 13px/1.4 "Pretendard"' }}>raw 데이터 쿼리 권한</div>
          </div>
          <div className={styles.guideRow}>
            <RoleChip role="viewer" />
            <div style={{ font: '600 13px/1.4 "Pretendard"' }}>대시보드 열람 권한</div>
          </div>
        </div>
      </div>

      {/* ── 멤버 리스트 ──────────────────────────────────── */}
      <div className={styles.list}>
        <div className={styles.head}>
          <div className={styles.headTitle}>
            현재 멤버
            <span className={styles.headTitleMuted}>{members.length}명</span>
          </div>
          {!adding && (
            <Btn
              variant="primary"
              icon={<NavIcon name="plus" size={14} color="currentColor" />}
              onClick={() => setAdding(true)}
            >
              {isAdmin ? '멤버 추가' : '멤버 추가 요청'}
            </Btn>
          )}
        </div>

        {adding && (
          <AddMemberForm
            existingEmails={members.map((m) => m.email)}
            isAdmin={isAdmin}
            isSubmitting={busyEmail !== null}
            onCancel={() => setAdding(false)}
            onSubmit={submitAddMember}
          />
        )}

        <div className={styles.tableHead}>
          <div>멤버</div>
          <div>역할</div>
          <div>부여일</div>
          <div>경로</div>
          <div style={{ textAlign: 'right' }}>액션</div>
        </div>

        {/* 멤버 목록 첫 로딩 — 텍스트 대신 스켈레톤. (규칙: orbit-loading-skeleton 스킬) */}
        {membersState.loading && !membersState.data && (
          <SkeletonText lines={4} lastWidth="50%" gap={12} lineHeight={14} />
        )}
        {!membersState.loading && members.length === 0 && (
          <div className={styles.empty}>등록된 멤버가 없어요</div>
        )}

        {members.map((m) => {
          const pending = pendingChanges[m.email];
          const isSelf = m.email === meta.current_user.email;
          const busy = busyEmail === m.email;
          return (
            <MemberRow
              key={`${m.email}-${m.scope}`}
              member={m}
              pending={pending}
              isSelf={isSelf}
              busy={busy}
              onChangeRole={(role) =>
                setPendingChanges((prev) => ({ ...prev, [m.email]: role }))
              }
              onCancelChange={() =>
                setPendingChanges((prev) => {
                  const next = { ...prev };
                  delete next[m.email];
                  return next;
                })
              }
              onSubmitChange={(role) => submitRoleChange(m, role)}
              onRemove={() => submitRemove(m)}
            />
          );
        })}
      </div>

      {/* 권한 반영 과정 스텝퍼 — admin 즉시 적용 시 단계별 진행 표시 */}
      <ProcessStepper
        steps={processSteps}
        title={processTitle}
        onClose={closeStepper}
      />
    </div>
  );
}
